import { useCallback, useState } from 'react';
import { Check, ChevronDown, ChevronRight, Mail, Pencil, Trash2, UserCheck, UserPlus, X } from 'lucide-react';
import { describeInviteOutcome, InviteUserResult, useOrgAdminUsers } from '../../../hooks/useOrgAdminUsers';
import { useUserProfile } from '../../../context/UserProfileContext';
import { CustomDropdown } from '../../../components/ui/CustomDropdown';
import { LoadingSpinner } from '../../../components/ui/LoadingSpinner';
import { SuccessToast } from '../../../components/ui/SuccessToast';
import { ConfirmModal } from '../../../components/admin/ConfirmModal';
import type { Organization, OrgUser } from '../../../types/admin';

// ── Types ────────────────────────────────────────────────────────────────

interface InviteTeamStepProps {
  org: Organization;
  orgId: string;
}

// ── Avatar helpers ──────────────────────────────────────────────────────

const AVATAR_COLORS = ['bg-plum', 'bg-rose', 'bg-midnight', 'bg-slate'];
function avatarColor(uid: string): string {
  const code = uid.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return AVATAR_COLORS[code % AVATAR_COLORS.length];
}

// ── Invite Form (bottom-sheet / modal) ──────────────────────────────────

function InviteForm({ onClose, onInvite, forceAdmin, dispatchEnabled, jobsEnabled }: {
  onClose: () => void;
  onInvite: (email: string, role: OrgUser['role']) => Promise<InviteUserResult>;
  forceAdmin?: boolean;
  dispatchEnabled?: boolean;
  jobsEnabled?: boolean;
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<OrgUser['role']>(forceAdmin ? 'admin' : 'estimator');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) { setError('Email is required.'); return; }
    setError('');
    setSubmitting(true);
    try {
      await onInvite(email.trim(), role);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invite.');
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-t-2xl px-4 pt-5 pb-8
                      md:rounded-2xl md:max-w-sm md:w-full md:pb-6">
        <div className="md:hidden w-10 h-1 bg-stone rounded-full mx-auto mb-5" />

        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-h3 text-midnight">Invite User</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate
                       hover:text-midnight hover:bg-mist transition-colors"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {error && (
            <div className="px-4 py-3 rounded-lg bg-rose/10 border border-rose/30 text-small text-rose">
              {error}
            </div>
          )}

          <div>
            <label
              htmlFor="invite-email"
              className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1"
            >
              Email Address <span className="text-rose">*</span>
            </label>
            <input
              id="invite-email"
              type="email"
              placeholder="user@company.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoFocus
              className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                         text-small text-midnight placeholder:text-slate
                         focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                         transition-colors duration-150"
            />
          </div>

          <div>
            <label
              htmlFor="invite-role"
              className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1"
            >
              Role
            </label>
            <CustomDropdown
              id="invite-role"
              value={role}
              onChange={val => setRole(val as OrgUser['role'])}
              options={[
                { value: 'estimator', label: 'Estimator', description: 'Creates and manages quotes' },
                { value: 'admin', label: 'Admin', description: 'Full organizational access' },
                { value: 'field_lead', label: 'Field Lead', description: 'Manager + field hybrid role' },
                ...(jobsEnabled !== false ? [{ value: 'mover', label: 'Mover', description: 'Executes jobs in the field' }] : []),
                ...(dispatchEnabled !== false ? [{ value: 'dispatcher', label: 'Dispatcher', description: 'Schedules and coordinates operations' }] : []),
              ]}
              placeholder="Select role…"
            />
            {forceAdmin && (
              <p className="text-[12px] text-amber-700 mt-1.5 leading-snug">
                This organization has no active admin. The first invited user must be an Admin.
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="h-11 rounded-lg bg-plum text-white text-small font-semibold
                       hover:bg-rose active:opacity-80 disabled:opacity-50
                       transition-colors duration-150"
          >
            {submitting ? 'Sending…' : 'Send Invite'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── User Row ────────────────────────────────────────────────────────────

function UserRow({ user, isSelf, isSoleAdmin, onUpdateRole, onRemove, onResendInvite, dispatchEnabled, jobsEnabled }: {
  user: OrgUser;
  isSelf: boolean;
  isSoleAdmin: boolean;
  onUpdateRole: (uid: string, role: OrgUser['role']) => Promise<OrgUser>;
  onRemove: (user: OrgUser) => void;
  onResendInvite?: (uid: string) => Promise<void>;
  dispatchEnabled?: boolean;
  jobsEnabled?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [soleAdminMsg, setSoleAdminMsg] = useState(false);
  const [role, setRole] = useState<OrgUser['role']>(user.role);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const isInvited = user.status === 'invited';

  function handleEditClick() {
    if (isSoleAdmin) { setSoleAdminMsg(true); return; }
    setEditing(true);
    setRole(user.role);
    setError('');
    setSoleAdminMsg(false);
  }

  async function handleSaveRole() {
    setSaving(true);
    setError('');
    try {
      await onUpdateRole(user.uid, role);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update role.');
    }
    setSaving(false);
  }

  return (
    <div className="bg-white border border-stone rounded-xl p-4 shadow-brand-sm">
      <div className="flex items-center gap-3">
        {isInvited ? (
          <div className="shrink-0 w-10 h-10 rounded-full bg-stone/60 flex items-center justify-center">
            <UserPlus size={16} className="text-slate" />
          </div>
        ) : user.photoBase64 ? (
          <img
            src={user.photoBase64}
            alt={user.displayName || user.email}
            className="shrink-0 w-10 h-10 rounded-full object-cover"
          />
        ) : (
          <div className={`shrink-0 w-10 h-10 rounded-full ${avatarColor(user.uid)} flex items-center justify-center`}>
            <span className="text-white font-display font-bold text-sm">
              {(user.displayName || user.email || '?').charAt(0).toUpperCase()}
            </span>
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {isInvited ? (
              <span className="text-sm italic text-slate/70 font-normal">invited</span>
            ) : (
              <span className="font-semibold text-small text-midnight truncate">
                {user.displayName || user.email}
              </span>
            )}
            {!editing && (
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold
                            uppercase tracking-wide leading-none
                            ${user.role === 'admin' ? 'bg-plum text-white' : user.role === 'mover' ? 'bg-blue-100 text-blue-700' : user.role === 'dispatcher' ? 'bg-amber-100 text-amber-700' : user.role === 'field_lead' ? 'bg-emerald-100 text-emerald-700' : 'bg-stone text-midnight'}`}
              >
                {user.role === 'admin' ? 'Admin' : user.role === 'mover' ? 'Mover' : user.role === 'dispatcher' ? 'Dispatcher' : user.role === 'field_lead' ? 'Field Lead' : 'Estimator'}
              </span>
            )}
          </div>
          <p className="text-[13px] text-slate mt-0.5 truncate">{user.email}</p>
        </div>

        {!editing ? (
          <button
            type="button"
            onClick={handleEditClick}
            className="shrink-0 h-8 w-8 flex items-center justify-center rounded-lg border border-stone
                       text-slate hover:border-plum hover:text-plum active:bg-mist
                       transition-colors duration-150"
            aria-label="Edit user"
          >
            <Pencil size={14} />
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="shrink-0 h-8 w-8 flex items-center justify-center rounded-lg border border-stone
                       text-slate hover:text-midnight active:bg-mist transition-colors duration-150"
            aria-label="Cancel edit"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Resend Invite */}
      {isInvited && !editing && onResendInvite && (
        <div className="mt-3">
          <button
            type="button"
            disabled={resending || resendSuccess}
            onClick={async () => {
              setResending(true);
              setError('');
              try {
                await onResendInvite(user.uid);
                setResendSuccess(true);
                setTimeout(() => setResendSuccess(false), 3000);
              } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to resend invite.');
              }
              setResending(false);
            }}
            className="h-9 px-3 rounded-lg border border-plum text-plum text-[13px] font-semibold
                       flex items-center gap-1.5
                       hover:bg-plum/5 active:bg-plum/10 disabled:opacity-50
                       transition-colors duration-150"
          >
            <Mail size={14} />
            {resendSuccess ? 'Invite Sent!' : resending ? 'Sending…' : 'Resend Invite'}
          </button>
          {error && <p className="text-[13px] text-rose mt-1.5">{error}</p>}
        </div>
      )}

      {/* Sole-admin notice */}
      {soleAdminMsg && !editing && (
        <div className="mt-3 px-3 py-2.5 rounded-lg bg-amber-50 border border-amber-200 text-[13px] text-amber-800 leading-snug">
          Your org only has one Admin. To edit this user, assign the Admin role to at least one other user.
        </div>
      )}

      {/* Inline edit panel */}
      {editing && (
        <div className="mt-4 pt-3 border-t border-stone flex flex-col gap-3">
          {error && <p className="text-[13px] text-rose">{error}</p>}

          <div>
            <label className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1">
              Role
            </label>
            <CustomDropdown
              value={role}
              onChange={val => setRole(val as OrgUser['role'])}
              options={[
                { value: 'estimator', label: 'Estimator', description: 'Creates and manages quotes' },
                { value: 'admin', label: 'Admin', description: 'Full organizational access' },
                { value: 'field_lead', label: 'Field Lead', description: 'Manager + field hybrid role' },
                ...(jobsEnabled !== false ? [{ value: 'mover', label: 'Mover', description: 'Executes jobs in the field' }] : []),
                ...(dispatchEnabled !== false ? [{ value: 'dispatcher', label: 'Dispatcher', description: 'Schedules and coordinates operations' }] : []),
              ]}
              placeholder="Select role…"
            />
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onRemove(user)}
              disabled={isSelf}
              className="h-10 px-4 rounded-lg border border-rose text-rose text-[13px] font-semibold
                         hover:bg-rose/5 active:bg-rose/10 disabled:opacity-40 disabled:cursor-not-allowed
                         transition-colors duration-150"
            >
              Remove
            </button>
            <button
              type="button"
              onClick={handleSaveRole}
              disabled={saving || role === user.role}
              className="flex-1 h-10 rounded-lg bg-plum text-white text-[13px] font-semibold
                         flex items-center justify-center gap-1.5
                         hover:bg-rose active:opacity-80 disabled:opacity-50
                         transition-colors duration-150"
            >
              {saving ? 'Saving…' : <><Check size={14} />Save Role</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────

export function InviteTeamStep({ org, orgId }: InviteTeamStepProps) {
  const { currentUser } = useUserProfile();
  const { users, loading, error, inviteUser, updateRole, removeUser, activateUser, resendInvite } = useOrgAdminUsers(orgId);
  const [isInviting, setIsInviting] = useState(false);
  const [showUserLimit, setShowSeatLimit] = useState(false);
  const [pendingRemove, setPendingRemove] = useState<OrgUser | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [activateError, setActivateError] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState('');
  const dismissToast = useCallback(() => setToastMessage(''), []);

  // Same behavior as Admin Panel › Company › Manage Users: existing members
  // with this email are reused and re-invited rather than duplicated.
  const handleInvite = useCallback(async (email: string, role: OrgUser['role']) => {
    const result = await inviteUser(email, role);
    setToastMessage(describeInviteOutcome(result));
    return result;
  }, [inviteUser]);

  const removingInvite = pendingRemove?.status === 'invited';
  const removingNeverActivated = removingInvite || pendingRemove?.neverActivated === true;
  const activeUsers = users.filter(u => u.status !== 'inactive');
  const inactiveUsers = users.filter(u => u.status === 'inactive');

  const maxUsers = org.maxUsers ?? null;
  const activeUserCount = activeUsers.length;
  const hasUserCap = maxUsers !== null;
  const hasCapacity = !hasUserCap || activeUserCount < maxUsers;
  const dispatchEnabled = org.featureDispatch !== false;
  const jobsEnabled = org.featureJobs !== false;

  const adminCount = activeUsers.filter(u => u.role === 'admin').length;
  const hasActiveAdmin = activeUsers.some(u => u.role === 'admin' && u.status === 'active');

  async function handleRemoveConfirm() {
    if (!pendingRemove) return;
    setRemoveError(null);
    const target = pendingRemove;
    try {
      const { deleted } = await removeUser(target.uid);
      setPendingRemove(null);
      setToastMessage(deleted
        ? `Invite for ${target.email} removed.`
        : `${target.displayName || target.email} deactivated.`);
    } catch (err) {
      setRemoveError(err instanceof Error ? err.message : 'Failed to remove user.');
    }
  }

  async function handleActivate(user: OrgUser) {
    setActivateError(null);
    try {
      await activateUser(user.uid);
    } catch (err) {
      setActivateError(err instanceof Error ? err.message : 'Failed to activate user.');
    }
  }

  return (
    <>
      <div className="space-y-4">
        {/* Invite button */}
        <button
          type="button"
          onClick={() => hasCapacity ? setIsInviting(true) : setShowSeatLimit(true)}
          className="w-full h-11 rounded-xl bg-plum text-white text-small font-semibold
                     flex items-center justify-center gap-2
                     hover:bg-rose active:opacity-80 transition-colors duration-150 mb-2"
        >
          <UserPlus size={18} />
          Invite User
        </button>

        {removeError && (
          <div className="px-4 py-3 rounded-lg bg-rose/10 border border-rose/30 text-small text-rose">
            {removeError}
          </div>
        )}
        {activateError && (
          <div className="px-4 py-3 rounded-lg bg-rose/10 border border-rose/30 text-small text-rose">
            {activateError}
          </div>
        )}

        {/* Users list */}
        {loading && (
          <div className="flex justify-center py-16">
            <LoadingSpinner size="lg" />
          </div>
        )}

        {!loading && error && (
          <div className="flex flex-col items-center py-16 gap-2 text-center">
            <p className="text-small text-rose font-semibold">Failed to load users</p>
            <p className="text-small text-slate">{error}</p>
          </div>
        )}

        {!loading && !error && (
          <>
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-3">
              Active Users ({hasUserCap ? `${activeUserCount} of ${maxUsers}` : activeUserCount})
            </p>
            {activeUsers.length === 0 ? (
              <div className="flex flex-col items-center py-16 gap-2 text-center">
                <p className="text-small text-slate">
                  No members yet. Invite your first user above.
                </p>
              </div>
            ) : (
              <ul className="flex flex-col gap-3">
                {activeUsers.map(user => (
                  <li key={user.uid}>
                    <UserRow
                      user={user}
                      isSelf={user.uid === currentUser?.uid}
                      isSoleAdmin={user.role === 'admin' && adminCount === 1}
                      onUpdateRole={updateRole}
                      onRemove={u => { setRemoveError(null); setPendingRemove(u); }}
                      onResendInvite={resendInvite}
                      dispatchEnabled={dispatchEnabled}
                      jobsEnabled={jobsEnabled}
                    />
                  </li>
                ))}
              </ul>
            )}

            {/* Inactive Users */}
            {inactiveUsers.length > 0 && (
              <div className="mt-6">
                <button
                  type="button"
                  onClick={() => setShowInactive(prev => !prev)}
                  className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-slate
                             hover:text-midnight transition-colors duration-150 mb-3"
                >
                  {showInactive ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  Inactive Users ({inactiveUsers.length})
                </button>
                {showInactive && (
                  <ul className="flex flex-col gap-3">
                    {inactiveUsers.map(user => (
                      <li key={user.uid}>
                        <div className="bg-white/60 border border-stone rounded-xl p-4 shadow-brand-sm opacity-75">
                          <div className="flex items-center gap-3">
                            <div className="shrink-0 w-10 h-10 rounded-full bg-stone/60 flex items-center justify-center">
                              <span className="text-slate font-display font-bold text-sm">
                                {(user.displayName || user.email || '?').charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold text-small text-slate truncate">
                                  {user.displayName || user.email}
                                </span>
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide leading-none bg-stone/50 text-slate">
                                  Inactive
                                </span>
                              </div>
                              <p className="text-[13px] text-slate/70 mt-0.5 truncate">{user.email}</p>
                            </div>
                            {user.neverActivated && (
                              <button
                                type="button"
                                onClick={() => { setRemoveError(null); setPendingRemove(user); }}
                                className="shrink-0 h-9 px-3 rounded-lg border border-rose text-rose text-[13px] font-semibold
                                           flex items-center gap-1.5
                                           hover:bg-rose/5 active:bg-rose/10 transition-colors duration-150"
                                aria-label={`Permanently remove ${user.email}`}
                              >
                                <Trash2 size={14} />
                                Remove
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleActivate(user)}
                              className="shrink-0 h-9 px-3 rounded-lg border border-plum text-plum text-[13px] font-semibold
                                         flex items-center gap-1.5
                                         hover:bg-plum/5 active:bg-plum/10 transition-colors duration-150"
                            >
                              <UserCheck size={14} />
                              Activate
                            </button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {isInviting && (
        <InviteForm
          onClose={() => setIsInviting(false)}
          onInvite={handleInvite}
          forceAdmin={!hasActiveAdmin}
          dispatchEnabled={dispatchEnabled}
          jobsEnabled={jobsEnabled}
        />
      )}

      <ConfirmModal
        isOpen={!!pendingRemove}
        title={removingInvite ? 'Remove invite?' : removingNeverActivated ? 'Permanently remove?' : 'Deactivate user?'}
        message={removingInvite
          ? `The pending invite for ${pendingRemove?.email} will be deleted. You can invite them again later.`
          : removingNeverActivated
            ? `${pendingRemove?.email} never activated their account. This record will be permanently deleted.`
            : `${pendingRemove?.displayName || pendingRemove?.email} will be deactivated and will no longer be able to access your organization. They can be reactivated later.`}
        confirmLabel={removingInvite ? 'Remove Invite' : removingNeverActivated ? 'Remove' : 'Deactivate'}
        variant="danger"
        onConfirm={handleRemoveConfirm}
        onClose={() => setPendingRemove(null)}
      />

      {hasUserCap && (
        <ConfirmModal
          isOpen={showUserLimit}
          title="User limit reached"
          message={`Your organization has reached its limit of ${maxUsers} user${maxUsers === 1 ? '' : 's'}. To invite a new user, upgrade your plan or deactivate an existing user.`}
          confirmLabel="Got it"
          variant="primary"
          onConfirm={() => setShowSeatLimit(false)}
          onClose={() => setShowSeatLimit(false)}
        />
      )}

      {toastMessage && (
        <SuccessToast message={toastMessage} onDismiss={dismissToast} durationMs={5000} />
      )}
    </>
  );
}
